import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare, faBan, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { FeedPerson, FeedRunCard, FeedSignal } from "@signalist/shared-types";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { collapsedSignalText } from "@/utils/signals/collapsedSignalText";
import { useAppDispatch } from "@/redux/hooks";
import { updateFeedSignalPriority } from "@/pages/Private/redux/feed/feed.slice";
import { useIgnoreTrackerSignalMutation } from "@/pages/Private/redux/signal/signal.api";
import { LinkedinSignalPost } from "@/pages/Private/pages/ProfileTracker/schema/trackerSignalResult";
import { SignalPreview } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/signalTypes";
import { LinkedinSignalPostPreview } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/LinkedinSignalPostPreview";
import { SignalIgnoreFeedbackModal } from "@/pages/Private/pages/ProfileTracker/components/CustomColumns/SignalTimeline/SignalIgnoreFeedbackModal";

import { SignalAiCta } from "./SignalAiCta";
import { buildSignalFeedbackContext } from "./runCardUtils";

const toSignalPreview = (signal: FeedSignal): SignalPreview => ({
	id: signal.signalId,
	status: "COMPLETED",
	priority: signal.priority,
	signalCategory: signal.signalCategory,
	title: signal.title,
	summary: signal.summary,
	sourceUrl: signal.sourceUrl,
	publishedDate: signal.publishedDate,
	reasoning: signal.reasoning,
	createdAt: signal.signalCollectedAt ?? signal.publishedDate ?? "",
	linkedinPostUrn: signal.linkedinPostUrn,
	linkedinPost: signal.linkedinPost as LinkedinSignalPost | null,
	linkedinCommentary: signal.linkedinCommentary,
});

interface SignalRowExpandProps {
	card: FeedRunCard;
	signal: FeedSignal;
	/** Feedback context target — the card's currently selected person (D5/D6). */
	selectedPerson: FeedPerson | null;
}

/**
 * Expanded detail of a signal one-liner: why-box, read-more with the full post
 * (LinkedIn payload or a generic web-source card) and the ignore-signal action
 * (D4 — the server auto-disqualifies via feedback `IGNORE`; the explicit reason
 * step is the confirmation, so no undo toast here).
 */
export const SignalRowExpand: FunctionComponent<SignalRowExpandProps> = ({
	card,
	signal,
	selectedPerson,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	// Opening a signal reveals the full post/source straight away — the toggle then
	// collapses it ("Show less") rather than being the gate to reveal it.
	const [isPostOpen, setIsPostOpen] = useState(true);
	const [isIgnoreOpen, setIsIgnoreOpen] = useState(false);
	const [ignoreError, setIgnoreError] = useState<string | null>(null);
	const [sendFeedback, { isLoading: isIgnoring }] = useIgnoreTrackerSignalMutation();

	const hasSource = !!signal.linkedinPost || !!signal.summary || !!signal.sourceUrl;
	const isLinkedinPost = signal.sourceType === "linkedin_post" || !!signal.linkedinPost;
	// The collapsed row now shows the summary (PREC-5037/5038), so the expanded body keeps the
	// stored title visible. The LinkedIn preview never shows the title, so its line is always up; the
	// generic web card does show it, so its line only takes over when that card is collapsed
	// ("Show less") — exactly one title is on screen at a time.
	const collapsed = collapsedSignalText({ title: signal.title, summary: signal.summary });
	const titleText = collapsedSignalText({ title: signal.title }).text;
	const isWebCardOpen = isPostOpen && hasSource && !isLinkedinPost;
	const showTitleLine =
		!!signal.title && collapsed.text !== titleText && (isLinkedinPost || !isWebCardOpen);

	const handleIgnoreSubmit = async ({ feedback }: { feedback: string }) => {
		setIgnoreError(null);

		try {
			await sendFeedback({
				signalId: signal.signalId,
				data: {
					action: "IGNORE",
					feedback,
					...buildSignalFeedbackContext(card, selectedPerson),
				},
			}).unwrap();

			dispatch(updateFeedSignalPriority({ signalId: signal.signalId, priority: "Disqualified" }));
			setIsIgnoreOpen(false);
		} catch {
			setIgnoreError(t("errors.wentWrong"));
		}
	};

	return (
		<div
			className="px-3.5 pb-3.5 pl-8 pt-0.5"
			data-testid={`feed-signal-expand-${signal.signalId}`}
		>
			{showTitleLine && (
				<div
					className="mb-2 text-[13px] font-semibold text-text"
					data-testid={`feed-signal-title-${signal.signalId}`}
				>
					{signal.title}
				</div>
			)}

			{signal.reasoning && (
				<div className="mb-3 rounded-lg bg-light-blue px-3.5 py-3">
					<div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-text-tertiary">
						{t("feed.runCard.signal.whyTitle")}
					</div>
					<p className="text-[13px] leading-relaxed text-text">{signal.reasoning}</p>
				</div>
			)}

			{isPostOpen &&
				hasSource &&
				(isLinkedinPost ? (
					<LinkedinSignalPostPreview signal={toSignalPreview(signal)} />
				) : (
					<div
						className="mb-3 rounded-lg border border-border-light bg-white p-3"
						data-testid={`feed-signal-web-source-${signal.signalId}`}
					>
						{signal.title && (
							<div className="mb-1 text-[13px] font-semibold text-text">{signal.title}</div>
						)}
						{signal.summary && (
							<p className="text-[13px] leading-relaxed text-text-secondary">{signal.summary}</p>
						)}
						{signal.sourceUrl && (
							<a
								className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-brand-light"
								href={signal.sourceUrl}
								rel="noreferrer"
								target="_blank"
								onClick={(event) => event.stopPropagation()}
							>
								{t("feed.runCard.signal.openOriginal")}
								<Icon className="h-2.5 w-2.5" icon={faArrowUpRightFromSquare} />
							</a>
						)}
					</div>
				))}

			{hasSource && (
				<button
					className="flex cursor-pointer items-center gap-1.5 py-1 text-[13px] font-semibold text-brand-light"
					data-testid={`feed-signal-read-more-${signal.signalId}`}
					type="button"
					onClick={() => setIsPostOpen((open) => !open)}
				>
					{isPostOpen ? t("feed.runCard.signal.showLess") : t("feed.runCard.signal.readMore")}
					<Icon
						className={classNames("h-3 w-3 transition-transform", isPostOpen ? "rotate-180" : "")}
						icon={faChevronDown}
					/>
				</button>
			)}

			<div className="mt-2.5 flex items-center gap-2.5">
				<button
					className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-border-light px-3 py-1.5 text-xs font-semibold text-status-error hover:bg-status-error-light disabled:cursor-not-allowed disabled:opacity-60"
					data-testid={`feed-signal-ignore-${signal.signalId}`}
					disabled={isIgnoring}
					type="button"
					onClick={() => setIsIgnoreOpen(true)}
				>
					<Icon className="h-3 w-3" icon={faBan} />
					{t("feed.runCard.signal.ignoreSignal")}
				</button>
				{/* P6 AI CTA: primary signal-seeded draft trigger or the job mirror (D4). */}
				<div className="ml-auto" data-testid="feed-signal-ai-slot">
					<SignalAiCta card={card} person={selectedPerson} signal={signal} variant="expand" />
				</div>
			</div>

			<SignalIgnoreFeedbackModal
				isOpened={isIgnoreOpen}
				isSubmitting={isIgnoring}
				submitError={ignoreError}
				onClose={() => setIsIgnoreOpen(false)}
				onSubmit={handleIgnoreSubmit}
			/>
		</div>
	);
};
